import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { OnboardingLayout } from '@/components/onboarding/OnboardingLayout';
import { OnboardingForm } from '@/components/onboarding/OnboardingForm';
import { userOnboardingService } from '@/services/userOnboardingService';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getInitialOnboardingStep } from '@/utils/authCredentials';
import { isSocialLoginEnabled } from '@/config/features';
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface UserOnboardingData {
  personalNumber: string;
  firstName: string;
  lastName: string;
}

type Step = 'choose' | 'password' | 'profile';

const MIN_PASSWORD_LENGTH = 8;

function getPasswordStrength(password: string) {
  const checks = {
    length: password.length >= MIN_PASSWORD_LENGTH,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  return { strength: Object.values(checks).filter(Boolean).length, checks };
}

export default function UserOnboarding() {
  const t = useTranslations('consultantOnboarding');
  const navigate = useNavigate();
  const { session } = useAuth();
  const [step, setStep] = useState<Step>(() => {
    const initialStep = getInitialOnboardingStep(session);
    return initialStep === 'choose' && !isSocialLoginEnabled() ? 'password' : initialStep;
  });

  const [loadingProvider, setLoadingProvider] = useState<'azure' | 'google' | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  const [formData, setFormData] = useState<UserOnboardingData>({
    personalNumber: '',
    firstName: '',
    lastName: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleProviderLink = async (provider: 'azure' | 'google') => {
    setLoginError(null);
    setLoadingProvider(provider);
    const { error: linkError } = await supabase.auth.linkIdentity({
      provider,
      options: { redirectTo: `${window.location.origin}/user-onboarding` },
    });
    if (linkError) {
      setLoginError(linkError.message);
      setLoadingProvider(null);
    }
    // On success the browser navigates to the provider; loading state is left as-is.
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    const { strength } = getPasswordStrength(password);
    if (password.length < MIN_PASSWORD_LENGTH || strength < 3) {
      setLoginError(t('userOnboarding.errors.passwordRules', { min: String(MIN_PASSWORD_LENGTH) }));
      return;
    }
    if (password !== confirmPassword) {
      setLoginError(t('userOnboarding.errors.passwordMismatch'));
      return;
    }

    setIsSettingPassword(true);
    const { error: updateError } = await supabase.auth.updateUser({
      password,
      data: { password_set: true },
    });
    setIsSettingPassword(false);

    if (updateError) {
      setLoginError(updateError.message);
      return;
    }
    setStep('profile');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.firstName.trim()) {
      setError(t('userOnboarding.errors.firstNameRequired'));
      setIsLoading(false);
      return;
    }

    if (!formData.lastName.trim()) {
      setError(t('userOnboarding.errors.lastNameRequired'));
      setIsLoading(false);
      return;
    }

    // Validate personal number format (12 digits)
    const personalNumberPattern = /^[0-9]{12}$/;
    if (!personalNumberPattern.test(formData.personalNumber)) {
      setError(t('userOnboarding.errors.personalNumberInvalid'));
      setIsLoading(false);
      return;
    }

    try {
      const result = await userOnboardingService.saveUserOnboardingData(formData);

      if (result.success) {
        // Navigate to overview - the guard will handle proper routing based on onboarding status
        navigate('/overview');
      } else {
        setError(result.error || t('userOnboarding.errors.saveFailed'));
      }
    } catch (err) {
      setError(userFacingErrorMessage(err, t('app.unexpectedError')));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof UserOnboardingData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;

    // For personal number, only allow digits and limit to 12
    if (field === 'personalNumber') {
      value = value.replace(/\D/g, '');
      if (value.length > 12) return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (step === 'choose') {
    return (
      <OnboardingLayout
        title={t('userOnboarding.choose.title')}
        description={t('userOnboarding.choose.description')}
      >
        <div className="space-y-3">
          {loginError && <p className="text-sm text-destructive">{loginError}</p>}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => handleProviderLink('azure')}
            disabled={loadingProvider !== null}
          >
            {loadingProvider === 'azure' ? (
              t('userOnboarding.choose.redirecting')
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0h10.931v10.931H0z" fill="#f25022" />
                  <path d="M12.069 0H23v10.931H12.069z" fill="#7fba00" />
                  <path d="M0 12.069h10.931V23H0z" fill="#00a4ef" />
                  <path d="M12.069 12.069H23V23H12.069z" fill="#ffb900" />
                </svg>
                {t('userOnboarding.choose.microsoft')}
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => handleProviderLink('google')}
            disabled={loadingProvider !== null}
          >
            {loadingProvider === 'google' ? (
              t('userOnboarding.choose.redirecting')
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {t('userOnboarding.choose.google')}
              </>
            )}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{t('userOnboarding.choose.or')}</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setStep('password')}
            disabled={loadingProvider !== null}
          >
            {t('userOnboarding.choose.setPassword')}
          </Button>
        </div>
      </OnboardingLayout>
    );
  }

  if (step === 'password') {
    const { checks } = getPasswordStrength(password);
    const requirements: Array<[keyof typeof checks, string]> = [
      ['length', t('userOnboarding.password.requirements.length', { min: String(MIN_PASSWORD_LENGTH) })],
      ['uppercase', t('userOnboarding.password.requirements.uppercase')],
      ['lowercase', t('userOnboarding.password.requirements.lowercase')],
      ['number', t('userOnboarding.password.requirements.number')],
      ['special', t('userOnboarding.password.requirements.special')],
    ];

    return (
      <OnboardingLayout title={t('userOnboarding.password.title')} description={t('userOnboarding.password.description')}>
        <OnboardingForm
          onSubmit={handlePasswordSubmit}
          isLoading={isSettingPassword}
          error={loginError}
          submitButtonText={t('userOnboarding.password.continue')}
          loadingText={t('userOnboarding.password.saving')}
        >
          <div className="space-y-2">
            <Label htmlFor="password">{t('userOnboarding.password.password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSettingPassword}
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {password && (
              <div className="space-y-1">
                {requirements.map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2 text-xs">
                    {checks[key] ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <X className="h-3 w-3 text-red-500" />
                    )}
                    <span className={checks[key] ? 'text-green-600' : 'text-red-600'}>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('userOnboarding.password.confirm')}</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSettingPassword}
              required
            />
          </div>
        </OnboardingForm>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      title={t('userOnboarding.profile.title')}
      description={t('userOnboarding.profile.description')}
    >
      <OnboardingForm
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={error}
        submitButtonText={t('userOnboarding.profile.continue')}
        loadingText={t('userOnboarding.profile.saving')}
      >
        <div className="space-y-2">
          <Label htmlFor="firstName">{t('userOnboarding.profile.firstName')}</Label>
          <Input
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleInputChange('firstName')}
            placeholder={t('userOnboarding.profile.firstNamePlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">{t('userOnboarding.profile.lastName')}</Label>
          <Input
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleInputChange('lastName')}
            placeholder={t('userOnboarding.profile.lastNamePlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="personalNumber">{t('userOnboarding.profile.personalNumber')}</Label>
          <Input
            id="personalNumber"
            type="text"
            value={formData.personalNumber}
            onChange={handleInputChange('personalNumber')}
            placeholder={t('userOnboarding.profile.personalNumberPlaceholder')}
            required
            disabled={isLoading}
            maxLength={12}
          />
          <p className="text-sm text-gray-500">
            {t('userOnboarding.profile.personalNumberHint')}
          </p>
        </div>
      </OnboardingForm>
    </OnboardingLayout>
  );
}
